import React, { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { useOnChange } from '../../../../../../helpers/hooks/useOnChange';
import { assertNotNull } from '../../../../../../helpers/nullHelpers';
import { PageLoadingIndicator } from '../../../../../../shared/LoadingIndicator';
import { QuoteOrDesignFormContext } from '../../../QuoteOrDesignFormContext';
import { manifoldFieldName, unitFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { UnitFieldContext } from '../../../units/UnitFieldContext';
import { JunctionTreeNode, NewTreeNode, NULL_CHILD, PumpTreeNode, TreeNode, TreeNodeType } from '../../treeNode';
import { getTreeWithStats } from '../getTreeWithStats';
import {
  addNodeToTreeWithoutStats,
  createInitialTreeWithHeatPumpRoot,
  deleteNodeAndChildrenFromTreeWithoutStats,
  markNodeAndChildrenForDeletionForTreeWithoutStats,
  ReferenceDataForTree,
  RoomStatsForTree,
  smartMarkNodeForRemovalFromTreeWithoutStats,
  smartRemoveEntryFromTreeWithoutStats,
  StatsData,
  Tree,
  TreeWithStats,
  unmarkForDeletionAllNodesInTree,
} from '../treeHelpers';
import { TreeStatus, validateTree } from '../treeValidator';
import { useUnitJsonField } from './useUnitJsonField';

export const useTree = () => {
  return useContext(TreeContext);
};

const TreeContext = createContext<{
  tree: TreeWithStats;
  treeStatus: TreeStatus;
  replaceNode: (treeNodeId: string, treeNode: TreeNode) => void;
  addLeaf: (nodeType: TreeNodeType, parentId: string, isFirstChild: boolean) => void;
  deleteEntryAndReconnectChildren: (node: PumpTreeNode | JunctionTreeNode, replaceWithFirstChild?: boolean) => void;
  deleteNodeAndChildren: (nodeId: string) => void;
  markNodeAndChildrenForDeletion: (nodeId: string) => void;
  smartMarkNodeForRemoval: (node: PumpTreeNode | JunctionTreeNode, replaceWithFirstChild?: boolean) => void;
  unmarkForDeletionAllNodes: () => void;
  getNewTreeNode: (nodeType: TreeNodeType) => NewTreeNode;
}>({
  tree: {
    DUMMY_ROOT: {
      id: 'DUMMY_ROOT',
      nodeType: TreeNodeType.HeatSource,
      markedForDeletion: false,
      firstChild: { ...NULL_CHILD },
      fieldData: {
        heatSource: 'Domestic Boiler & Pump',
      },
      stats: {
        generatedPressureInKilopascals: null,
        indexCircuitStats: {
          availablePressureInKilopascals: null,
          pumpCapacityPercentage: null,
          requiredPressureInKilopascals: null,
        },
        state: 'OK',
        exitPipe: {
          flowRateInLitresPerMinute: null,
          velocityInMetresPerSecond: null,
          pressureDropInKilopascals: null,
          state: 'OK',
        },
      },
    },
  },
  treeStatus: { incompleteReasons: [], hasError: false },
  replaceNode: () => null,
  addLeaf: () => null,
  deleteEntryAndReconnectChildren: () => null,
  deleteNodeAndChildren: () => null,
  markNodeAndChildrenForDeletion: () => null,
  smartMarkNodeForRemoval: () => null,
  unmarkForDeletionAllNodes: () => null,
  getNewTreeNode: () => ({
    nodeType: TreeNodeType.HeatSource,
    markedForDeletion: false,
    deletionRoot: false,
    fieldData: { heatSource: null },
    firstChild: { ...NULL_CHILD },
  }),
});

type Props = PropsWithChildren<object>;

export const TreeContextProvider = ({ children }: Props) => {
  const { designCalculations, unit, unitIndex } = assertNotNull(useContext(UnitFieldContext));
  const {
    formProps: { setFieldValue },
    referenceData,
  } = assertNotNull(useContext(QuoteOrDesignFormContext));
  const unitDesignCalculations = designCalculations?.response ?? null;

  const pumpsByManifoldIndex = unit.manifolds.map(manifold => manifold.pump);
  const rooms: Array<RoomStatsForTree> | null =
    unitDesignCalculations?.rooms.map(room => ({
      manifoldIndex: room.manifoldIndex,
      flowRateInLitresPerMinute: room.heatOutput?.flowRateInLitresPerMinute ?? null,
      pressureLossPerCoil: room.pressureLossPerCoil ?? null,
      pipeDiameterInMillimetres:
        unit.manifolds?.[room.manifoldIndex]?.rooms?.[room.roomIndex]?.floorConstruction?.pipeDiameterInMillimetres ??
        null,
      numberOfPorts: Number(unit.manifolds?.[room.manifoldIndex]?.rooms?.[room.roomIndex].numberOfPorts) ?? null,
    })) ?? null;

  const referenceDataForTree: ReferenceDataForTree = {
    unitPumpOptions: referenceData.unitPumpOptions.map(option => option.value),
    manifoldPumpOptions: referenceData.manifoldPumpOptions.map(option => option.value),
    pipeOptions: referenceData.pipeOptions.map(option => option.value),
    heatSourceOptions: referenceData.heatSourceOptions.map(option => option.value),
  };

  const statsData: StatsData = { pumpsByManifoldIndex, referenceData: referenceDataForTree, rooms };

  const { value: tree, setValue: setTree } = useUnitJsonField<Tree>(unitIndex, 'pipelineMapJson');

  useEffect(() => {
    if (tree === null) {
      const initialTreeWithoutStats = createInitialTreeWithHeatPumpRoot();
      setTree(initialTreeWithoutStats);
    }
  }, [tree, setTree]);

  useOnChange(() => {
    if (tree) {
      Object.values(tree).forEach(node => {
        if (
          node.nodeType === TreeNodeType.Manifold &&
          node.fieldData.manifoldIndex !== null &&
          unit.manifolds.length > node.fieldData.manifoldIndex
        ) {
          if (node.fieldData.pumpOnManifold) {
            setFieldValue(
              manifoldFieldName(unitIndex, node.fieldData.manifoldIndex, 'pump'),
              referenceData.manifoldPumpOptions[1].value,
            );
          } else {
            setFieldValue(manifoldFieldName(unitIndex, node.fieldData.manifoldIndex, 'pump'), null);
          }
        }
      });

      const pumps = Object.values(tree)
        .map(node => {
          if (node.nodeType === TreeNodeType.Pump) {
            return referenceData.unitPumpOptions.find(
              pumpOption => pumpOption.value?.displayName === node.fieldData.pump,
            )?.value;
          }
        })
        .filter(pump => !!pump);

      setFieldValue(unitFieldName(unitIndex, 'pumps'), pumps);
    }
  }, [unit.pipelineMapJson]);

  if (tree === null) {
    return <PageLoadingIndicator />;
  }

  const treeWithStats = getTreeWithStats(tree, statsData);
  const treeStatus = validateTree(treeWithStats, unit.manifolds.filter(manifold => !manifold.isRadiator).length);

  const getNewTreeNode = (nodeType: TreeNodeType): NewTreeNode => {
    switch (nodeType) {
      case TreeNodeType.HeatSource:
        return { nodeType, markedForDeletion: false, fieldData: { heatSource: null }, firstChild: { ...NULL_CHILD } };

      case TreeNodeType.Junction:
        return {
          nodeType,
          markedForDeletion: false,
          fieldData: null,
          firstChild: { ...NULL_CHILD },
          secondChild: { ...NULL_CHILD },
        };

      case TreeNodeType.Manifold:
        return {
          nodeType,
          markedForDeletion: false,
          fieldData: {
            manifoldIndex: null,
            pumpOnManifold: null,
          },
        };

      case TreeNodeType.Pump:
        return {
          nodeType,
          markedForDeletion: false,
          fieldData: { pump: null, includesBlendingValve: null },
          firstChild: { ...NULL_CHILD },
        };
    }
  };

  const addLeaf = (nodeType: TreeNodeType, parentId: string, isFirstChild: boolean) => {
    setTree(addNodeToTreeWithoutStats(tree, getNewTreeNode(nodeType), parentId, isFirstChild));
  };

  const replaceNode = (treeNodeId: string, treeNode: TreeNode) => {
    setTree({ ...tree, [treeNodeId]: treeNode });
  };

  const deleteEntryAndReconnectChildren = (
    treeNode: PumpTreeNode | JunctionTreeNode,
    replaceWithFirstChild?: boolean,
  ) => {
    setTree(smartRemoveEntryFromTreeWithoutStats(tree, treeNode, replaceWithFirstChild));
  };

  const markNodeAndChildrenForDeletion = (treeNodeId: string) => {
    setTree(markNodeAndChildrenForDeletionForTreeWithoutStats(tree, treeNodeId));
  };

  const smartMarkNodeForRemoval = (treeNode: PumpTreeNode | JunctionTreeNode, replaceWithFirstChild?: boolean) => {
    setTree(smartMarkNodeForRemovalFromTreeWithoutStats(tree, treeNode, replaceWithFirstChild));
  };

  const deleteNodeAndChildren = (treeNodeId: string) => {
    setTree(deleteNodeAndChildrenFromTreeWithoutStats(tree, treeNodeId));
  };

  const unmarkForDeletionAllNodes = () => {
    setTree(unmarkForDeletionAllNodesInTree(tree));
  };

  return (
    <TreeContext.Provider
      value={{
        tree: treeWithStats,
        treeStatus,
        replaceNode,
        addLeaf,
        getNewTreeNode,
        deleteEntryAndReconnectChildren,
        deleteNodeAndChildren,
        markNodeAndChildrenForDeletion,
        smartMarkNodeForRemoval,
        unmarkForDeletionAllNodes,
      }}
    >
      {children}
    </TreeContext.Provider>
  );
};
